import React, { useState } from 'react';
import axios from 'axios';

interface UploadResult {
    call_id: number;
    call_uuid: string;
    phone: string;
    lead_id: number | null;
    duration_seconds: number;
    provider: string;
    call_type: string | null;
    call_status: string | null;
    agent_name: string | null;
    agent_crm_uuid: string | null;
    recorded_at: string | null;
    uploaded_at: string;
    recording_url: string;
    s3_key: string;
    s3_bucket: string;
    s3_region: string;
    file_size_bytes: number;
    error?: string;
}

const RESULT_ROWS: [keyof UploadResult, string][] = [
    ['s3_key', 'S3 Key'],
    ['s3_bucket', 'S3 Bucket'],
    ['s3_region', 'S3 Region'],
    ['file_size_bytes', 'File Size'],
    ['call_id', 'Call ID'],
    ['call_uuid', 'Call UUID'],
    ['phone', 'Phone'],
    ['lead_id', 'Lead ID'],
    ['duration_seconds', 'Duration'],
    ['provider', 'Provider'],
    ['call_type', 'Call Type'],
    ['call_status', 'Call Status'],
    ['agent_name', 'Agent Name'],
    ['agent_crm_uuid', 'Agent CRM UUID'],
    ['recorded_at', 'Recorded At'],
    ['uploaded_at', 'Uploaded At'],
    ['recording_url', 'Source URL'],
];

function formatValue(key: keyof UploadResult, val: UploadResult[keyof UploadResult]): string {
    if (val === null || val === undefined) return '—';
    if (key === 'file_size_bytes') return `${((val as number) / 1024).toFixed(1)} KB`;
    if (key === 'duration_seconds') return `${val} s`;
    return String(val);
}

const PocRecordingUpload: React.FC = () => {
    const [count, setCount] = useState(1);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<UploadResult[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleUpload = async () => {
        setLoading(true);
        setResults([]);
        setError(null);
        try {
            const base = import.meta.env.VITE_API_URL || '/api';
            const res = await axios.post(`${base}/poc/upload-recording`, { count });
            if (res.data?.success) {
                const data = res.data.data;
                setResults(Array.isArray(data) ? data : [data]);
            } else {
                setError(res.data?.message || 'Upload failed');
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <div style={{ borderBottom: '1px solid #e5e7eb', background: '#fff', width: '100%', padding: '12px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img
                src="https://www.zeroharm.in/cdn/shop/files/Zeroharm_logo_1.png"
                alt="Zeroharm"
                style={{ height: '36px', objectFit: 'contain' }}
            />
        </div>

        <div style={{ fontFamily: 'monospace', padding: '32px', maxWidth: '720px', margin: '0 auto' }}>
            <h2>POC — Upload Call Recordings to S3</h2>
            <p style={{ color: '#555', fontSize: '13px' }}>
                Finds the most recent call logs with a recording URL and duration &gt; 10 s,
                downloads them, and pushes to S3 with full metadata.
            </p>

            <div style={{ marginTop: '20px' }}>
                <span style={{ fontSize: '13px', color: '#444', marginRight: '12px' }}>Number of recordings:</span>
                {[1, 2, 3, 4, 5].map(n => (
                    <button
                        key={n}
                        onClick={() => setCount(n)}
                        style={{
                            marginRight: '6px',
                            width: '36px',
                            height: '36px',
                            fontSize: '14px',
                            cursor: 'pointer',
                            background: count === n ? '#1a56db' : '#f3f4f6',
                            color: count === n ? '#fff' : '#333',
                            border: count === n ? '2px solid #1a56db' : '2px solid #d1d5db',
                            borderRadius: '4px',
                            fontWeight: count === n ? 'bold' : 'normal',
                        }}
                    >
                        {n}
                    </button>
                ))}
            </div>

            <button
                onClick={handleUpload}
                disabled={loading}
                style={{
                    marginTop: '16px',
                    padding: '10px 24px',
                    fontSize: '14px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    background: loading ? '#999' : '#1a56db',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                }}
            >
                {loading ? 'Uploading…' : `Upload ${count} Recording${count > 1 ? 's' : ''} to S3`}
            </button>

            {error && (
                <pre style={{ marginTop: '20px', background: '#fff0f0', border: '1px solid #f99', padding: '12px', borderRadius: '4px', color: '#c00', whiteSpace: 'pre-wrap' }}>
                    ERROR: {error}
                </pre>
            )}

            {results.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                    <p style={{ color: 'green', fontWeight: 'bold', marginBottom: '16px' }}>
                        ✓ {results.filter(r => !r.error).length} of {results.length} upload{results.length > 1 ? 's' : ''} successful
                    </p>
                    {results.map((result, i) => (
                        <div key={result.call_id} style={{ marginBottom: '24px', border: `1px solid ${result.error ? '#fca5a5' : '#e5e7eb'}`, borderRadius: '6px', overflow: 'hidden' }}>
                            <div style={{ background: result.error ? '#fff1f2' : '#f9fafb', padding: '8px 12px', borderBottom: `1px solid ${result.error ? '#fca5a5' : '#e5e7eb'}`, fontSize: '13px', fontWeight: 'bold', color: result.error ? '#b91c1c' : '#374151' }}>
                                Recording {i + 1} — {result.phone || `Call #${result.call_id}`}
                                {!result.error && <> &nbsp;·&nbsp; {result.duration_seconds} s &nbsp;·&nbsp; {result.provider}</>}
                            </div>
                            {result.error ? (
                                <div style={{ padding: '10px 12px', fontSize: '13px', color: '#b91c1c' }}>
                                    ERROR: {result.error}
                                </div>
                            ) : (
                                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
                                    <tbody>
                                        {RESULT_ROWS.map(([key, label]) => (
                                            <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '6px 12px 6px 12px', color: '#666', width: '140px', verticalAlign: 'top' }}>{label}</td>
                                                <td style={{ padding: '6px 12px', wordBreak: 'break-all' }}>{formatValue(key, result[key])}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
        </>
    );
};

export default PocRecordingUpload;
